import { useEffect, useMemo, useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { DateTime24 } from "./DateTime24";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { bookJobForLater } from "../lib/api";
import { addDaysIso, tenantTodayIso, wallToDate } from "../lib/tenantTime";
import { tenantLocale, tenantTz } from "../lib/company";

/**
 * BookForLaterDialog — pending-tile companion dialog (Feb 2026).
 *
 * Future-dates a pending job so a customer who rang in asking for a
 * Monday 9am tow gets scheduled into Tow Booked rather than sitting
 * in the live queue immediately. Backend endpoint enforces the same
 * 2h minimum we show here, so the Save button cannot submit a value
 * that would shuttle back to pending on the next release-loop tick.
 */

const MIN_HOURS_AHEAD = 2;

// Default: tomorrow 09:00 company-local — the single most common "book this
// for later" case in a tow yard (next business day start).
const defaultBookFor = () => `${addDaysIso(tenantTodayIso(), 1)}T09:00`;

export default function BookForLaterDialog({ job, open, onOpenChange, onBooked }) {
  const [value, setValue] = useState(defaultBookFor);
  const [saving, setSaving] = useState(false);

  // Reset the picker to its default every time the dialog is reopened.
  // Without this, a dispatcher who cancels then reopens on another job
  // would still see the previously-typed time.
  useEffect(() => {
    if (open) setValue(defaultBookFor());
  }, [open]);

  const chosenDate = useMemo(() => (value ? wallToDate(value) : null), [value]);
  const msAhead = chosenDate ? chosenDate.getTime() - Date.now() : 0;
  const minMs = MIN_HOURS_AHEAD * 60 * 60 * 1000;
  const tooSoon = !chosenDate || Number.isNaN(chosenDate.getTime()) || msAhead <= minMs;

  const releaseAt = useMemo(() => {
    if (!chosenDate || Number.isNaN(chosenDate.getTime())) return null;
    return new Date(chosenDate.getTime() - minMs);
  }, [chosenDate, minMs]);

  const submit = async () => {
    if (!job || tooSoon) return;
    setSaving(true);
    try {
      await bookJobForLater(job.id, chosenDate.toISOString());
      toast.success(
        `Booked for ${chosenDate.toLocaleString(tenantLocale(), { timeZone: tenantTz(),
          dateStyle: "medium",
          timeStyle: "short",
          hourCycle: "h23",
        })}`,
      );
      onBooked?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to book job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="book-for-later-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-indigo-600" />
            Book for Later
          </DialogTitle>
          <DialogDescription>
            {job ? (
              <>
                Schedule <strong>{job.customer_name}</strong>
                {job.address ? <> · {job.address}</> : null} for a future pickup
                time. The job moves to <strong>Tow Booked</strong> and auto-
                releases to the driver 2 hours before pickup.
              </>
            ) : (
              "Schedule this job for a future pickup time."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="book-for-later-when" className="uppercase-label">
              Pickup date &amp; time
            </Label>
            <div className="mt-1">
              <DateTime24
                value={value}
                onChange={setValue}
                dataTestId="book-for-later-when-input"
              />
            </div>
          </div>

          {tooSoon ? (
            <div
              className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900/40 dark:text-amber-200 px-2.5 py-1.5"
              data-testid="book-for-later-too-soon"
            >
              Must be at least {MIN_HOURS_AHEAD} hours from now. Jobs auto-release to
              the driver {MIN_HOURS_AHEAD} hours before the booked time, so a shorter
              window would shuttle the job straight back into the live queue.
            </div>
          ) : (
            <div
              className="text-xs rounded-md border border-indigo-200 bg-indigo-50 text-indigo-900 dark:bg-indigo-950/30 dark:border-indigo-900/40 dark:text-indigo-200 px-2.5 py-1.5"
              data-testid="book-for-later-release-hint"
            >
              Will release to driver at{" "}
              <strong>
                {releaseAt?.toLocaleString(tenantLocale(), { timeZone: tenantTz(),
                  dateStyle: "medium",
                  timeStyle: "short",
                  hourCycle: "h23",
                })}
              </strong>
              .
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="book-for-later-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || tooSoon}
            data-testid="book-for-later-save-btn"
          >
            <CalendarClock className="h-4 w-4 mr-1.5" />
            {saving ? "Booking…" : "Book"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
